import { useCallback, useMemo } from 'react';

import { useAnalyticsContext } from './context';

export type CashFlowGroupBy = 'DAILY' | 'WEEKLY' | 'MONTHLY';

type TrackFn = (eventName: string, data: Record<string, unknown>) => void;

type CashFlowTracker = {
  viewSelected: (params: {
    viewId: string;
    viewName: string;
    isDefault: boolean;
    allowedGroupBy?: string;
    dataSource?: string;
    currency?: string | null;
    useScenarios?: boolean;
  }) => void;

  groupByChanged: (params: {
    previousGroupBy: CashFlowGroupBy | string;
    newGroupBy: CashFlowGroupBy | string;
  }) => void;

  scenarioChanged: (params: {
    previousScenarioId?: string | null;
    newScenarioId?: string | null;
  }) => void;

  chartTypeChanged: (params: {
    previousChartType: string;
    newChartType: string;
    groupBy: CashFlowGroupBy | string;
  }) => void;

  chartModalOpened: (params: {
    chartType: string;
    groupBy: CashFlowGroupBy | string;
    hasDrillDown: boolean;
  }) => void;

  cellDetailOpened: (params: {
    categoryId?: string | null;
    categoryName?: string | null;
    rowType?: string | null;
    groupBy: CashFlowGroupBy | string;
    isUncategorized?: boolean;
  }) => void;

  fullscreenToggled: (params: { entering: boolean }) => void;

  exported: (params: {
    viewId?: string | null;
    groupBy: CashFlowGroupBy | string;
    rowCount?: number | null;
  }) => void;

  targetsSaved: (params: {
    viewId?: string | null;
    groupBy: CashFlowGroupBy | string;
    createdCount?: number;
    updatedCount?: number;
    deletedCount?: number;
  }) => void;

  projectedSaved: (params: {
    viewId?: string | null;
    groupBy: CashFlowGroupBy | string;
    createdCount?: number;
    updatedCount?: number;
    deletedCount?: number;
  }) => void;

  transactionAdded: (params: {
    addType: string;
    viewId?: string | null;
  }) => void;

  manageViewsOpened: () => void;

  // Settings / configuration
  viewSaved: (params: {
    viewId?: string | null;
    viewName?: string | null;
    isNew: boolean;
    isDefault?: boolean;
    cloneFromId?: string | null;
    dataSource?: string | null;
    allowedGroupBy?: string | null;
    currency?: string | null;
    useScenarios?: boolean;
    showCategories?: boolean;
    showCreditDebit?: boolean;
  }) => void;

  viewDeleted: (params: {
    viewId: string;
    viewName?: string | null;
  }) => void;

  categoryCreated: (params: {
    viewId?: string | null;
    flowType?: string | null;
    parentId?: string | null;
  }) => void;

  categoryEdited: (params: {
    viewId?: string | null;
    categoryId: string;
    flowType?: string | null;
  }) => void;

  categoryDeleted: (params: {
    viewId?: string | null;
    categoryId: string;
    flowType?: string | null;
    isDefaultView?: boolean;
  }) => void;

  categoryReordered: (params: {
    viewId?: string | null;
    movedCategoryId: string;
    newParentId?: string | null;
    flowType?: string | null;
  }) => void;
};

export const useCashFlowTracking = (): CashFlowTracker => {
  const analytics = useAnalyticsContext();

  const track: TrackFn = useCallback((eventName, data) => {
    try {
      analytics?.mixPanel?.trackContinueEvent?.({
        customEvent: eventName,
        module: 'cash_flow',
        ...data,
      });
    } catch {
      // Tracking must never break the UI.
    }
  }, [analytics]);

  return useMemo<CashFlowTracker>(() => ({
    viewSelected: (params) => track('cash_flow_view_selected', params),
    groupByChanged: (params) => track('cash_flow_group_by_changed', params),
    scenarioChanged: (params) => track('cash_flow_scenario_changed', params),
    chartTypeChanged: (params) => track('cash_flow_chart_type_changed', params),
    chartModalOpened: (params) => track('cash_flow_chart_modal_opened', params),
    cellDetailOpened: (params) => track('cash_flow_cell_detail_opened', params),
    fullscreenToggled: (params) => track('cash_flow_fullscreen_toggled', params),
    exported: (params) => track('cash_flow_exported', params),
    targetsSaved: (params) => track('cash_flow_targets_saved', params),
    projectedSaved: (params) => track('cash_flow_projected_saved', params),
    transactionAdded: (params) => track('cash_flow_transaction_added', params),
    manageViewsOpened: () => track('cash_flow_manage_views_opened', {}),
    viewSaved: (params) => track('cash_flow_view_saved', params),
    viewDeleted: (params) => track('cash_flow_view_deleted', params),
    categoryCreated: (params) => track('cash_flow_category_created', params),
    categoryEdited: (params) => track('cash_flow_category_edited', params),
    categoryDeleted: (params) => track('cash_flow_category_deleted', params),
    categoryReordered: (params) => track('cash_flow_category_reordered', params),
  }), [track]);
};
